'use client';

import { ArrowRight, Search, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import type { SalaryInsightsIndexData } from '../types';

import styles from '../salary-insights.module.css';

import { FaqSection } from './FaqSection';
import { PromoBox } from './PromoBox';
import {
  FeaturedSalaryGuidesSection,
  SalaryInsightsAeoSection,
  SalaryInsightsRelatedResourcesSection,
} from './SalarySeoSections';

interface SalaryInsightsIndexPageProps {
  data: SalaryInsightsIndexData;
}

export function SalaryInsightsIndexPage({ data }: SalaryInsightsIndexPageProps) {
  const [query, setQuery] = useState('');

  const filteredCategories = useMemo(() => {
    if (!query.trim()) {
      return data.categories;
    }
    const normalized = query.toLowerCase();
    return data.categories
      .map((category) => ({
        ...category,
        professions: category.professions.filter(
          (profession) =>
            profession.label.toLowerCase().includes(normalized) ||
            category.title.toLowerCase().includes(normalized),
        ),
      }))
      .filter((category) => category.professions.length > 0);
  }, [data.categories, query]);

  const hasResults = filteredCategories.length > 0;

  return (
    <main className="min-h-screen bg-white">
      {/* Hero banner */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#e0f7fa] via-white to-[#f0fdfa] pb-28 pt-20 text-slate-900 md:pb-32 md:pt-28">
        <div
          aria-hidden="true"
          className={`${styles.heroBlobLight} -left-20 -top-20 h-72 w-72 bg-[#3bbdad]/20 md:h-96 md:w-96`}
        />
        <div
          aria-hidden="true"
          className={`${styles.heroBlobLight} -bottom-32 -right-16 h-80 w-80 bg-[#0ea5e9]/15 md:h-[28rem] md:w-[28rem]`}
        />
        <div
          aria-hidden="true"
          className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-[#3bbdad]/10 blur-3xl"
        />

        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#3bbdad]/20 bg-white/70 px-4 py-1.5 text-sm font-semibold text-[#0f766e] shadow-sm backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-[#3bbdad]" />
            2026 Compensation Data
          </span>
          <h1 className="mb-6 text-balance text-4xl font-extrabold leading-tight tracking-tight md:text-5xl lg:text-6xl">
            {data.h1}
          </h1>
          <p className="text-balance text-lg leading-relaxed text-slate-600 md:text-xl">
            {data.intro}
          </p>
        </div>
      </section>

      <FeaturedSalaryGuidesSection />

      {/* CTA banner */}
      <section className="border-b border-[#3bbdad]/10 bg-[#f0fdfa] py-4">
        <div className="mx-auto max-w-7xl px-4 text-center text-sm text-[#0f766e] sm:px-6 lg:px-8 md:text-base">
          <span dangerouslySetInnerHTML={{ __html: data.ctaBanner }} />
        </div>
      </section>

      {/* Categories search */}
      <section className="bg-slate-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto mb-12 max-w-2xl">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
              <Search className="h-5 w-5" />
            </div>
            <Input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search for your profession (e.g., Data Analyst)..."
              className="h-14 rounded-2xl border-0 bg-white pl-12 pr-5 text-base shadow-md shadow-slate-900/5 ring-1 ring-slate-100 transition-shadow placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#3bbdad]"
              data-testid="profession-search"
            />
          </div>

          {!hasResults && (
            <p
              className="text-center text-lg text-slate-500"
              data-testid="no-results-message"
            >
              No matching salary guides found. Try another search!
            </p>
          )}

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCategories.map((category) => (
              <div
                key={category.title}
                className={`rounded-2xl border border-slate-100 bg-white p-6 shadow-sm ${styles.cardLift}`}
              >
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-2xl">
                    {category.icon}
                  </span>
                  <h3 className="text-xl font-bold text-slate-900">{category.title}</h3>
                </div>
                <ul className="space-y-1">
                  {category.professions.map((profession) => (
                    <li key={profession.slug}>
                      <a
                        href={`/salary-insights/${profession.slug}.html`}
                        className="group flex items-center justify-between rounded-xl px-3 py-2.5 text-slate-700 transition-colors hover:bg-slate-50 hover:text-[#3bbdad]"
                      >
                        {profession.label}
                        <ArrowRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-[#3bbdad]" />
                      </a>
                    </li>
                  ))}
                  <li className="px-3 py-2 text-sm text-slate-400">More guides coming soon...</li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-center text-white shadow-xl shadow-slate-900/10 md:p-12">
            <h3 className="mb-3 text-2xl font-bold md:text-3xl">Are you earning what you deserve?</h3>
            <p className="mb-8 text-lg text-white/80">
              Ensure your resume highlights the top skills needed to negotiate a higher salary.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-auto rounded-full bg-[#3bbdad] px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-[#3bbdad]/25 transition-transform hover:-translate-y-0.5 hover:bg-[#3bbdad]/90"
              >
                <a href="/resume-analyzer">Check ATS Resume Score</a>
              </Button>
              <Button
                asChild
                className="h-auto rounded-full bg-white px-7 py-3.5 text-base font-semibold text-slate-900 transition-transform hover:-translate-y-0.5 hover:bg-slate-100"
              >
                <a href="/salary-insights-report/">Get Salary Report</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <SalaryInsightsAeoSection />
      <SalaryInsightsRelatedResourcesSection />
      <PromoBox />
      <FaqSection faq={data.faq} />
    </main>
  );
}
